import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { ButtonExportCSVAsync, ButtonSwitch, Cell, DateRangePicker, Empty, FieldWrapper, Filter, FilterButton, HeaderCell, HeaderRow, Input, PaginationFooter, Row, Section, Select, SortLabel, Table, TableBody, TableHead } from 'components';
import { appConfig } from 'config/app';
import ListContext, { ListContextProvider } from 'providers/ListContextProvider';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getAcceptanceLedgerRequest, getPrefundLedgerRequest, Ledger, selectLedgerEntries } from 'store/Collections';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { selectSiteAcceptance } from 'store/Site';
import { IStore } from 'store/types';
import { ApiQueryOperator, IAcceptanceLedgerEntry, IApiList, IApiQueryParam, ILedgerEntry, TransactionReferenceType } from 'types';
import { CSVFormatter, IExportCSV } from 'utils/csvformatter';
import { centsToDollarsDisplay } from 'utils/currency';
import { formatDateForApi, formatDateForUI, getTimeWithZone } from 'utils/date';
import * as API from '../../api';
import { ActiveReportTab, IParams, LedgerRow, ReportTableHeader } from './components';

const ledgerFilterTypesOptions = [
  {
    value: undefined,
    label: 'All',
  },
  {
    value: {
      key: 'entry_type',
      operator: ApiQueryOperator.in,
      value: ['credit'],
    },
    label: 'Credit',
  },
  {
    value: {
      key: 'entry_type',
      operator: ApiQueryOperator.in,
      value: ['debit'],
    },
    label: 'Debit',
  },
  {
    value: {
      key: 'entry_type',
      operator: ApiQueryOperator.in,
      value: ['pending_credit'],
    },

    label: 'Pending Credit',
  },
];

interface IDependenciesType {
  currentSiteIdDep: string;
  currentOrganizationIdDep: string;
}

const getRelatedTxnId = (entry: ILedgerEntry | IAcceptanceLedgerEntry) => (entry.txnRefType === TransactionReferenceType.PAYOUT || entry.txnRefType === TransactionReferenceType.ACCEPTANCE_PAYMENT ? entry.txnRefId : '');

const ReportsLedger = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { option = ActiveReportTab.Prefunded } = useParams<IParams>();
  const DEFAULT_END_DATE = dayjs(new Date()).format('MM-DD-YYYY');
  const DEFAULT_START_DATE = dayjs(new Date()).subtract(6, 'day').format('MM-DD-YYYY');
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteAcceptance = useSelector(selectSiteAcceptance());
  const ledger = useSelector(selectLedgerEntries());
  const total = ledger?.total;
  const ledgerEntries = ledger?.data;
  const [filterActive, setFilterActive] = useState<boolean>(false);

  const acceptanceChecked = option === ActiveReportTab.Acceptance;

  const fetchData = (queryParams: IApiQueryOptions) => {
    const meta = queryParams.meta as IDependenciesType;
    const { currentSiteIdDep } = meta;
    const startDate = (queryParams.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
    const endDate = (queryParams.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;

    const qWithoutDates = (queryParams.q || []).filter((q: IApiQueryParam) => q.key !== 'payment_date');
    const queryParamsWithoutMeta = omit(queryParams, 'meta');

    if (currentSiteIdDep && startDate && endDate) {
      if (!acceptanceChecked) {
        dispatch(
          getPrefundLedgerRequest(currentSiteIdDep, startDate as string, endDate as string, {
            ...queryParamsWithoutMeta,
            q: qWithoutDates,
          })
        );
      } else {
        dispatch(
          getAcceptanceLedgerRequest(currentSiteIdDep, startDate as string, endDate as string, {
            ...queryParamsWithoutMeta,
            q: qWithoutDates,
          })
        );
      }
    }
  };
  const isLoadingPrefund = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Ledger.GET_PREFUND_LEDGER_REQUEST));
  const isLoadingAcceptance = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Ledger.GET_ACCEPTANCE_LEDGER_REQUEST));
  const isLoading = isLoadingPrefund || isLoadingAcceptance;

  useEffect(() => {
    if (acceptanceChecked && (!currentSite?.id || (currentSiteAcceptance && !currentSiteAcceptance.enabled))) {
      history.push(routeConstants.private.reportsLedger);
    }
  }, [history, acceptanceChecked, currentSite, currentSiteAcceptance]);

  const onSwitchChange = useCallback(() => {
    history.push(`${routeConstants.private.reportsLedger}/${acceptanceChecked ? ActiveReportTab.Prefunded : ActiveReportTab.Acceptance}`);
  }, [acceptanceChecked]);

  const exportCSV = async (acceptanceChecked: boolean, siteId: string, startDate: string, endDate: string, options: IApiQueryOptions): Promise<IExportCSV> => {
    if (!acceptanceChecked) {
      const response: ApiResponse<IApiList<ILedgerEntry>> = await API.Sites.listPrefundLedgerEntries(siteId, startDate, endDate, options);
      return CSVFormatter.ledger(response.body);
    }
    const response: ApiResponse<IApiList<IAcceptanceLedgerEntry>> = await API.Sites.listAcceptanceLedgerEntries(siteId, startDate, endDate, options);
    return CSVFormatter.ledger(response.body);
  };

  const getEmployeeName = (data: ILedgerEntry | IAcceptanceLedgerEntry) => {
    if ('employee' in data) {
      return data.employee.name;
    }
    if ('employeeName' in data) {
      return data.employeeName;
    }
    return '';
  };

  return (
    <>
      {currentSite && (
        <ListContextProvider
          fetchData={fetchData}
          totalSelector={() => total}
          defaultOffset={0}
          defaultLimit={10}
          defaultSort="created_at"
          dependencies={{
            currentSiteIdDep: currentSite.id,
            acceptanceChecked,
          }}
          defaultFilters={[
            {
              key: 'payment_date',
              operator: ApiQueryOperator.gte,
              value: formatDateForApi(DEFAULT_START_DATE),
            },
            {
              key: 'payment_date',
              operator: ApiQueryOperator.lte,
              value: formatDateForApi(DEFAULT_END_DATE),
            },
          ]}
          dataKey="reportsLedger"
        >
          <ListContext.Consumer>
            {({ methods, query, components }) => {
              const { onFiltersChange, onSortChange, isSortDesc } = methods;
              const { resultsPerPage: ResultsPerPage, pagination: Pager } = components;
              const debounceMs = 500;

              const { q } = query;
              const qWithoutDates = (q || []).filter((q: IApiQueryParam) => q.key !== 'payment_date');
              const startDate = (q || []).find((q: IApiQueryParam) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
              const endDate = (q || []).find((q: IApiQueryParam) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;

              const queryParamsWithoutMeta = omit(query, 'meta');
              const isDescendingSort = isSortDesc();

              const setDates = ([start, end]: string[]) => {
                const otherFilters = (q || []).filter((q: IApiQueryParam) => q.key !== 'payment_date');
                const isValid = dayjs(start, 'YYYY-MM-DD', true).isValid() && dayjs(end, 'YYYY-MM-DD', true).isValid();
                if (isValid) {
                  (() => {
                    const filters = [
                      ...otherFilters,
                      {
                        key: 'payment_date',
                        operator: ApiQueryOperator.gte,
                        value: start,
                      },
                      {
                        key: 'payment_date',
                        operator: ApiQueryOperator.lte,
                        value: end,
                      },
                    ];
                    onFiltersChange(filters, true);
                  })();
                }
              };

              const onTypeChange = (value: any) => {
                const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'entry_type') as IApiQueryParam[];
                const filters = value && value[0].value ? [...otherFilters, value[0].value] : otherFilters;
                onFiltersChange(filters);
              };

              const onDescriptionChange = (desc: string) => {
                const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'description') as IApiQueryParam[];
                const filters =
                  desc.trim() !== ''
                    ? [
                        ...otherFilters,
                        {
                          key: 'description',
                          operator: ApiQueryOperator.like,
                          value: desc,
                        },
                      ]
                    : otherFilters;
                onFiltersChange(filters);
              };

              const debouncedOnDescriptionChange = debounce(onDescriptionChange, debounceMs);

              return (
                <Section data-cy="table">
                  <ReportTableHeader
                    title="Results"
                    count={total || 0}
                    actions={
                      <>
                        <DateRangePicker
                          fluidWidth={false}
                          inputHeight="small"
                          onSubmit={setDates}
                          pickerPosition="right"
                          startDate={DEFAULT_START_DATE}
                          endDate={DEFAULT_END_DATE}
                          submitLabel="apply"
                          maxDays={appConfig.DATE_RANGE.MAX_DAYS_3_MONTHS}
                        />
                        {currentSiteAcceptance?.enabled && <ButtonSwitch onChange={onSwitchChange} checked={acceptanceChecked} leftText="Prefunded" rightText="Tip Acceptance" data-cy="acceptance-switch" />}
                        <FilterButton filterActive={filterActive} appliedFilters={qWithoutDates.length} onClick={() => setFilterActive(!filterActive)} />
                        <ButtonExportCSVAsync
                          disabled={!(total && total >= 1) || isLoading}
                          exportFunc={() => {
                            const options = { ...queryParamsWithoutMeta, offset: 0, limit: 0, q: qWithoutDates };
                            return exportCSV(acceptanceChecked, currentSite.id, startDate as string, endDate as string, options);
                          }}
                          fileName={`Ledger_${currentSite.name}_${acceptanceChecked ? `${ActiveReportTab.Acceptance}_` : ''}${startDate}_${endDate}`}
                        />
                      </>
                    }
                    isLoading={isLoading}
                  />
                  <Filter isVisible={filterActive}>
                    <FieldWrapper label="Type" data-cy="filter-type">
                      <Select
                        placeholder="All"
                        multi={false}
                        values={[]}
                        options={ledgerFilterTypesOptions}
                        onChange={(value) => {
                          onTypeChange(value);
                        }}
                        data-cy="type-filter"
                      />
                    </FieldWrapper>
                    <FieldWrapper label="Description" data-cy="filter-description">
                      <Input placeholder="Description" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnDescriptionChange(e.currentTarget.value)} />
                    </FieldWrapper>
                  </Filter>
                  <Table sticky scrollable>
                    <TableHead>
                      <HeaderRow sticky>
                        <HeaderCell>
                          <SortLabel data-cy="date-header" label="Date" isSorted isSortedDesc={isDescendingSort} onClick={() => onSortChange('created_at')} isLoading={isLoading} />
                        </HeaderCell>

                        <HeaderCell data-cy="type-header">{!isLoading && 'Type'}</HeaderCell>

                        <HeaderCell data-cy="description-header">{isLoading ? '' : 'Description'}</HeaderCell>

                        <HeaderCell align="left" data-cy="employee-header">
                          {!isLoading && 'Employee'}
                        </HeaderCell>

                        <HeaderCell align="left" data-cy="transaction-id-header">
                          {!isLoading && 'Transaction ID'}
                        </HeaderCell>

                        <HeaderCell align="right" data-cy="amount-header">
                          {!isLoading && 'Amount'}
                        </HeaderCell>

                        <HeaderCell align="right" data-cy="new-balance-header">
                          {!isLoading && 'New Balance'}
                        </HeaderCell>
                      </HeaderRow>
                    </TableHead>

                    <TableBody>
                      {!isLoading && ledgerEntries?.length === 0 ? (
                        <Row>
                          <Cell colSpan={8}>
                            <Empty container containerHeight={300} icon="Payment" message="No results. Try a different date range." />
                          </Cell>
                        </Row>
                      ) : (
                        ledgerEntries?.map((datum: ILedgerEntry | IAcceptanceLedgerEntry) => (
                          <LedgerRow
                            data-cy="ledger-row"
                            data-value={datum.id}
                            trxId={datum.id}
                            key={`ledger-entry-${datum.id}`}
                            amount={centsToDollarsDisplay(datum.entryType === 'debit' ? -datum.amount : datum.amount)}
                            date={formatDateForUI(datum.createdAt)}
                            description={datum.description}
                            notes={datum.txnRefNotes}
                            isLoading={isLoading}
                            newBalance={centsToDollarsDisplay(datum.balance)}
                            relatedId={getRelatedTxnId(datum)}
                            time={getTimeWithZone(datum.createdAt)}
                            employeeName={getEmployeeName(datum)}
                            type={datum.entryType}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                  <PaginationFooter>
                    {!isLoading && !!total && total > query.limit && ResultsPerPage}
                    {!isLoading && !!total && total > query.limit && Pager}
                  </PaginationFooter>
                </Section>
              );
            }}
          </ListContext.Consumer>
        </ListContextProvider>
      )}
    </>
  );
};

export default ReportsLedger;
